import $ from 'jquery';
import { FUNC_NOOP } from 'util/constants';
import { $body } from 'util/global';
import Raven from 'util/raven';

/**
 * Generic controllers allow for an HTML element to specify an attribute such as
 * data-controller="app/someModule:someMethod" Which will then get called once the element is
 * loaded. It can be called thru a refresh, component framework, or a direct call to
 * initGenericControllers.
 */

/**
 * Drills down into the context for things like Foo.Bar.Zip.Zap.fun.
 * @param {object} myModule - Either window or a module loaded from require.js
 * @param {{context: {}, name: String}} controllerInfo
 * @param {[]} args - the arguments passed to the function we're calling
 */
function findControllerAndCall(myModule, controllerInfo, args) {
  const captureErrorMessage = msg => {
    const errorMsg = `Invalid controller: ${
      controllerInfo.context ? `${controllerInfo.context}:` : ''
    } ${controllerInfo.name}: ${msg}`;
    Raven.captureMessage(errorMsg);
    // eslint-disable-next-line no-console
    console.error(errorMsg);
  };
  const namespaces = controllerInfo.name.split('.');
  const functionName = namespaces.pop();
  let newNamespace = myModule;
  // drill down thru the namespaces and make sure they exist
  for (let i = 0; i < namespaces.length; i++) {
    newNamespace = newNamespace[namespaces[i]];
    if (typeof newNamespace !== 'object') {
      captureErrorMessage(`${namespaces[i]} is not an object`);
      return;
    }
  }
  if (typeof newNamespace[functionName] !== 'function') {
    captureErrorMessage(`${functionName} is not a function`);
    return;
  }
  /**
   * Does the actual call to the function given a context and handles errors.
   * 'myModule' in this context is the module which is sent as the "this" for apply
   * 'newNamespace' - the object or module the function belongs to
   * 'functionName' - the name of the function to call
   */
  newNamespace[functionName].apply(myModule, args);
}

function getControllerNameAndContext(controllerPath) {
  // If the value in 'data-controller="controllerPath" is
  // app/foo:bar then app/foo will be the name of the module
  const [context, name] = controllerPath.split(':');
  if (name === undefined) {
    Raven.captureMessage(`Controller name is undefined for: ${controllerPath}`);
  }
  return { context, name };
}

/**
 * Calls function by its name in a given context with given args.
 * @param functionName
 * @param args - any additional args for the function (up to 100)
 * @returns {Promise} - the promise resolves if the module and function call are successful
 */
export function executeFunctionByName(functionName, ...args) {
  // We need to combine old_args with any additional arguments from the magical "arguments"
  const controllerInfo = getControllerNameAndContext(functionName);

  return new Promise((resolve, reject) => {
    const handleSuccess = context => {
      findControllerAndCall(context, controllerInfo, args);
      resolve();
    };

    const handleError = (() => {
      const errors = {};
      return e => {
        // throwing the error causes this to be called again, so make sure it's only called
        // once (but still throw the error)
        if (!errors[controllerInfo.context]) {
          errors[controllerInfo.context] = controllerInfo.context;
          Raven.captureMessage(`[module error] ${functionName} (${window.location.pathname})`);
          reject(e);
        }
      };
    })();

    let context;
    try {
      const subdirectory = controllerInfo.context.split(/\//g)[0];
      switch (subdirectory) {
        case 'app':
          // eslint-disable-next-line global-require, import/no-dynamic-require
          context = require(`app/${controllerInfo.context.replace('app/', '')}`);
          break;
        case 'util':
          // eslint-disable-next-line global-require, import/no-dynamic-require
          context = require(`@/util/${controllerInfo.context.replace('util/', '')}.js`);
          break;
        default:
          reject(new Error(`Subdirectory ${subdirectory} is not setup for generic controllers.`));
      }
    } catch (e) {
      handleError(e);
    }
    if (context) {
      handleSuccess(context);
    }
  });
}

/**
 * Initializes data-controller attributes
 * @param {JQuery} [$root] - Root element to limit searching
 * @param {object} [opts]
 */
export function initGenericControllers($root = $body, opts) {
  const theOpts = Object.assign(
    {
      callback: FUNC_NOOP,
      force: false,
      ignoreHidden: false,
    },
    opts,
  );

  // We only want to call the callback once after all the data-controllers finish loading
  let $elems = $root.find('[data-controller]').add($root.filter('[data-controller]'));
  if (!theOpts.force) {
    $elems = $elems.not('[data-controller-inited]');
  }

  if (theOpts.ignoreHidden) {
    $elems = $elems.filter(':visible');
  }

  const promises = [];
  $elems.each((_, elem) => {
    const $element = $(elem);
    const controllers = $element.data('controller');

    $element.attr('data-controller-inited', true);

    if (controllers) {
      controllers.split(',').forEach(controller => {
        const trimmedController = controller.trim();
        if (trimmedController.length) {
          promises.push(executeFunctionByName(trimmedController, $element));
        }
      });
    }
  });
  Promise.all(promises).then(theOpts.callback);
}
